import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import { getEnv } from "../lib/env";

/**
 * Mesure de la boucle participants → organisateurs (A7 phase 0).
 *
 * Tout passe par la RPC `track_funnel_event` (validation, limite de débit,
 * dédoublonnage en base). Événements :
 * - `shown`            : le CTA « crée ton sondage » est affiché (une fois par
 *                        participant et par sondage, garanti par la base) ;
 * - `clicked`          : enregistré CÔTÉ SERVEUR par `/api/go` avant la
 *                        redirection (cf. goHref) — un insert navigateur suivi
 *                        d'une navigation mourait avec la page ;
 * - `vote_completed`   : déclencheur sur poll_responses (pas d'appel client) ;
 * - `create_started`   : ouverture d'une page de création ;
 * - `create_published` : déclencheur sur polls ; le client n'ajoute que la
 *                        provenance `ref` (attribution) ;
 * - `poll_shared`      : l'organisateur copie / partage le lien (trackShared) ;
 * - `poll_closed`, `second_poll_created`, `poll_created_from_participant` :
 *                        déclencheurs en base, aucun appel client.
 *
 * Provenance `ref` : vote | share | ics | final | ai. Elle arrive dans l'URL
 * (`?ref=`), est gardée le temps de la session d'onglet (sessionStorage), puis
 * rattachée à la publication.
 *
 * Aucune empreinte d'appareil : la clé de dédoublonnage de `shown` est le
 * jeton de vote aléatoire déjà existant, haché et salé par sondage en base.
 * Tout est best-effort : un échec ne bloque ni n'affiche jamais rien.
 */

export type CtaVariant = "A" | "B" | "C";
export type FunnelRef = "vote" | "share" | "ics" | "final" | "ai";
export type ShareChannel = "copy" | "whatsapp" | "native" | "email" | "qr";
type ClientFunnelEvent = "shown" | "create_started" | "create_published";

const REFS: readonly FunnelRef[] = ["vote", "share", "ics", "final", "ai"];
const REF_STORAGE_KEY = "dd_funnel_ref";
const STARTED_STORAGE_KEY = "dd_funnel_create_started";

export function parseFunnelRef(search: string): FunnelRef | null {
  const params = new URLSearchParams(search);
  const ref = params.get("ref");
  if (ref && (REFS as readonly string[]).includes(ref)) return ref as FunnelRef;
  // Ancien lien du CTA de fin de vote (`/create?cta=A`), encore en cache.
  if (params.get("cta")) return "vote";
  return null;
}

function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Stockage indisponible (navigation privée, navigateur intégré) : tant pis.
  }
}

export class CtaTracker {
  static async track(
    event: ClientFunnelEvent,
    options: {
      pollId?: string | null;
      ref?: FunnelRef | null;
      variant?: CtaVariant | null;
      participant?: string | null;
    } = {},
  ): Promise<void> {
    try {
      const { error } = await supabase.rpc("track_funnel_event", {
        p_event: event,
        p_poll_id: options.pollId ?? null,
        p_ref: options.ref ?? null,
        p_participant: options.participant ?? null,
        p_variant: options.variant ?? null,
      });
      if (error) {
        logger.debug("CtaTracker.track skipped", "analytics", {
          event,
          message: error.message,
        });
      }
    } catch (err) {
      logger.debug("CtaTracker.track failed", "analytics", {
        event,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /**
   * `poll_shared` : l'organisateur copie ou partage le lien de vote. Une fois
   * par sondage et par canal (garanti en base). Envoyé en `keepalive` : le
   * partage WhatsApp ou natif fait souvent quitter la page aussitôt, et une
   * requête ordinaire mourrait avec elle (c'est ce qui perdait les clics).
   */
  static trackShared(
    pollId: string | null | undefined,
    channel: ShareChannel,
  ): void {
    if (!pollId) return;
    const url = getEnv("VITE_SUPABASE_URL");
    const anonKey = getEnv("VITE_SUPABASE_ANON_KEY");
    if (!url || !anonKey || typeof fetch !== "function") return;
    try {
      void fetch(`${url}/rest/v1/rpc/track_funnel_event`, {
        method: "POST",
        keepalive: true,
        headers: {
          apikey: anonKey,
          Authorization: `Bearer ${anonKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          p_event: "poll_shared",
          p_poll_id: pollId,
          p_channel: channel,
        }),
      }).catch(() => undefined);
    } catch {
      // Best-effort.
    }
  }

  /** Lien du CTA : passe par `/api/go`, qui compte le clic puis redirige. */
  static goHref(options: {
    to?: "create" | "new";
    ref: FunnelRef;
    pollId?: string | null;
    variant?: CtaVariant | null;
  }): string {
    const params = new URLSearchParams({
      to: options.to ?? "create",
      ref: options.ref,
    });
    if (options.pollId) params.set("poll", options.pollId);
    if (options.variant) params.set("v", options.variant);
    return `/api/go?${params.toString()}`;
  }

  /** Mémorise la provenance trouvée dans l'URL et la renvoie (ou la précédente). */
  static rememberRef(search: string): FunnelRef | null {
    const ref = parseFunnelRef(search);
    if (ref) writeSession(REF_STORAGE_KEY, ref);
    return ref ?? CtaTracker.rememberedRef();
  }

  static rememberedRef(): FunnelRef | null {
    const ref = readSession(REF_STORAGE_KEY);
    return ref && (REFS as readonly string[]).includes(ref)
      ? (ref as FunnelRef)
      : null;
  }

  /** `create_started`, une fois par onglet et par page de création. */
  static trackCreateStarted(page: string, search: string): void {
    const ref = CtaTracker.rememberRef(search);
    const marker = `${page}:${ref ?? ""}`;
    if (readSession(STARTED_STORAGE_KEY) === marker) return;
    writeSession(STARTED_STORAGE_KEY, marker);
    void CtaTracker.track("create_started", { ref });
  }

  /** Rattache la provenance mémorisée à un sondage tout juste publié. */
  static attributePublished(pollId: string | null | undefined): void {
    const ref = CtaTracker.rememberedRef();
    if (!pollId || !ref) return;
    void CtaTracker.track("create_published", { pollId, ref });
  }
}
